<template>
  <div class="scaled-preview" :style="{ width: scaledWidth + 'px', height: scaledHeight + 'px'}">

    <div v-if="hasOverflow" class="overflow-indicator">
      Overflows by: {{ overflowWidth }}px (W), {{ overflowHeight }}px (H)
    </div>

    <div
      class="certificate-html-rendered"
      :style="{ width: maxWidth + 'px', height: maxHeight + 'px', transform: `scale(${scaleX}, ${scaleY})` }"
      ref="certificateContainer"
      v-html="certificateHtml"
    ></div>
  </div>
</template>

<script>
export default {
  name: "CertificatePreview",
  props: {
    templateContent: { type: String, required: true },
    placeholders: { type: Object, default: () => ({}) },
    description: String,
    fullName: String,
    certificateTitle: String,

    // These numbers are for server defined numbers of server page size
    maxWidth: { type: Number, default: 1150 },
    maxHeight: { type: Number, default: 812 },
    scaleX: { type: Number, default: 0.5 },
    scaleY: { type: Number, default: 0.4 },
  },
  data() {
    return {
      templateHtml: "",
      certificateHtml: "",
      overflowWidth: 0,
      overflowHeight: 0,
    };
  },
  computed: {
    hasOverflow() {
      return this.overflowWidth > 0 || this.overflowHeight > 0;
    },
    formattedDate() {
      const options = { year: "numeric", month: "long", day: "2-digit" };
      return new Date().toLocaleDateString("en-US", options);
    },
    scaledWidth() {
      return this.maxWidth * this.scaleX;
    },
    scaledHeight() {
      return this.maxHeight * this.scaleY;
    },
  },
  watch: {
    templateContent: {
      immediate: true,
      handler(newVal) {
        if (newVal && newVal.trim()) {
          this.templateHtml = newVal.trim();
          this.updateCertificate();
        }
      },
    },

    placeholders: {
      deep: true,
      handler() {
        this.updateCertificate();
      },
    },
    description: "updateCertificate",
    fullName: "updateCertificate",
    certificateTitle: "updateCertificate",
  },
  methods: {
    escapeRegExp(str) {
      return String(str).replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    },

    formatDateValue(val) {
      const options = { year: "numeric", month: "long", day: "2-digit" };
      if (!val && val !== 0) return this.formattedDate;
      if (val instanceof Date && !isNaN(val)) return val.toLocaleDateString("en-US", options);

      const parsed = new Date(val);
      if (!isNaN(parsed)) return parsed.toLocaleDateString("en-US", options);

      return String(val);
    },

    updateCertificate() {
      if (!this.templateHtml) return;

      let html = this.templateHtml;

      const map = this.placeholders || {};
      
      Object.keys(map).forEach((key) => {
        if (key === "IssueDate") return; 
        const value = map[key] != null ? String(map[key]) : "";
        const regex = new RegExp(`{{\\s*${this.escapeRegExp(key)}\\s*}}`, "g");
        html = html.replace(regex, value);
      });

      if (!map.UserFullName && this.fullName) {
        html = html.replace(/{{\s*UserFullName\s*}}/g, this.fullName);
      }

      if (!map.CertificateTitle && this.certificateTitle) {
        html = html.replace(/{{\s*CertificateTitle\s*}}/g, this.certificateTitle);
      }

      if (!map.Description && this.description) {
        html = html.replace(/{{\s*Description\s*}}/g, this.description);
      }

      const issueVal = (this.placeholders && this.placeholders.IssueDate) ? this.formatDateValue(this.placeholders.IssueDate) : this.formattedDate;
      const validVal = (this.placeholders && this.placeholders.ValidUntil) ? this.formatDateValue(this.placeholders.ValidUntil) : this.formattedDate;

      html = html.replace(/{{\s*IssueDate\s*}}/g, issueVal);
      html = html.replace(/{{\s*ValidUntil\s*}}/g, validVal);

      this.certificateHtml = html;
      this.$nextTick(this.checkOverflow);
    },

    checkOverflow() {
      const element = this.$refs.certificateContainer;
      if (!element) {
        this.overflowWidth = 0;
        this.overflowHeight = 0;
        this.$emit("onOverflowChange", { width: 0, height: 0, hasOverflow: false });
        return;
      }

      const widthOverflow = Math.max(0, element.scrollWidth - this.maxWidth);
      const heightOverflow = Math.max(0, element.scrollHeight - this.maxHeight);

      this.overflowWidth = widthOverflow;
      this.overflowHeight = heightOverflow;

      this.$emit("onOverflowChange", {
        width: widthOverflow,
        height: heightOverflow,
        hasOverflow: widthOverflow > 0 || heightOverflow > 0,
      });
    },
  },
};
</script>

<style scoped>
.scaled-preview {
  position: relative;
  overflow: visible;
  border: none;
  box-sizing: border-box;
}

.certificate-html-rendered {
  transform-origin: top left;
  overflow: visible;
}

.overflow-indicator {
  position: absolute;
  top: 8px;
  left: 8px;
  background: rgba(255, 0, 0, 0.9);
  color: white;
  padding: 4px 8px;
  font-size: 22px;
  border-radius: 4px;
  z-index: 10;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.3);
}
</style>
